import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { NotificationResponse } from '@orion/contracts';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotificationsQuery, useUnreadCountQuery } from './api.js';
import { notificationLink } from './links.js';

type Tab = 'all' | 'unread' | 'mentions' | 'assigned';

const TYPE_STYLE: Record<string, { bg: string; fg: string; path: string; category: string; isRect?: boolean }> = {
  'lead.created': { bg: '#FFF4D6', fg: '#8A5A06', path: 'm12 2 2.6 5.9 6.4.6-4.8 4.3 1.4 6.2L12 15.9 6.4 19l1.4-6.2L3 8.5l6.4-.6z', category: 'Lead' },
  'lead.assigned': { bg: '#ECEFFD', fg: '#2439B3', path: 'M12 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8', category: 'Lead' },
  'note.mention': { bg: '#ECEFFD', fg: '#2439B3', path: 'M12 8v4l.01 4M12 3a9 9 0 1 0 5.3 16.3', category: 'Note' },
  'task.reminder': { bg: '#FEF3E2', fg: '#8A4B06', path: 'M12 7v5l3 2', category: 'Task' },
  'deal.won': { bg: '#E6F4EC', fg: '#1E7A4C', path: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z', category: 'Opportunity' },
  'deal.lost': { bg: '#FBE9E7', fg: '#B42318', path: 'M18 6 6 18M6 6l12 12', category: 'Opportunity' },
  'import.completed': { bg: '#ECEFFD', fg: '#2439B3', path: 'M12 16V4M7 9l5-5 5 5 M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3', category: 'Import', isRect: true },
  'webhook.disabled': { bg: '#FDECEA', fg: '#B42318', path: 'M12 9v4M12 17h.01M12 3 2 20h20z', category: 'Integrations' },
  'pulse.lead_created': { bg: '#ECEFFD', fg: '#2E47D6', path: 'm3 8.5 9 6.5 9-6.5 M4 19.5h4l1.3-2.5 2 4 1.6-3.3 1.1 1.8h6', category: 'Inbox Pulse', isRect: true },
  'pulse.reply_received': { bg: '#ECEFFD', fg: '#2E47D6', path: 'm3 8.5 9 6.5 9-6.5 M4 19.5h4l1.3-2.5 2 4 1.6-3.3 1.1 1.8h6', category: 'Inbox Pulse', isRect: true },
  'pulse.reconnect_reminder': { bg: '#FDF1E2', fg: '#8A4B06', path: 'M12 9v4M12 17h.01M12 3 2 20h20z', category: 'Inbox Pulse' },
  'calendly.meeting_booked': { bg: '#E6F4EC', fg: '#1E7A4C', path: 'M3 10h18M8 3v4M16 3v4 M3 5h18v16H3z', category: 'Calendly', isRect: true },
  'calendly.meeting_canceled': { bg: '#FBE9E7', fg: '#B42318', path: 'M3 10h18M8 3v4M16 3v4 M3 5h18v16H3z', category: 'Calendly', isRect: true },
};
const DEFAULT_TYPE_STYLE: (typeof TYPE_STYLE)[string] = { bg: '#EFEEEA', fg: '#5E6470', path: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0', category: 'Update' };

/** Rows that get the "Admins only" badge treatment — a full-width tinted
 * card instead of the plain unread-dot row, since both of these are
 * org-visibility alerts rather than something the viewer personally did.
 * `webhook.disabled` keeps its original danger/red look; `lead.created` is
 * the same pattern in a celebratory gold instead, not an urgent warning. */
const ADMIN_ALERT_STYLE: Record<string, { bg: string; border: string; title: string; body: string; meta: string }> = {
  'webhook.disabled': { bg: 'var(--color-danger-soft)', border: '#F2C4BE', title: 'var(--color-danger)', body: 'var(--color-danger)', meta: '#8F1C13' },
  'lead.created': { bg: '#FFF8E6', border: '#F0D590', title: '#8A5A06', body: '#8A5A06', meta: '#8A5A06' },
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function groupLabel(iso: string): 'Today' | 'Earlier' {
  return new Date(iso).toDateString() === new Date().toDateString() ? 'Today' : 'Earlier';
}

/** Topbar's notification bell (design/orion-crm-design/screenshots/
 * Notifications.png — that "screen" is this dropdown panel open over the
 * dashboard, not a standalone route). The backend (list/unread-count/mark-
 * read/mark-all-read, and every notification `type` this renders —
 * lead.assigned/note.mention/task.reminder/deal.won/deal.lost/
 * import.completed/webhook.disabled — already existed).
 *
 * No Snooze or Reply — both need real features this pass didn't build (a
 * snoozed-until column + filtering; an inline reply-to-note flow), so a
 * "View" button is the only per-row action. No email-digest line — no
 * digest-scheduling feature exists; it links to the real notification
 * settings page instead. */
export function NotificationBell() {
  // design/orion-crm-design/rendered-html/Notifications.html captures this
  // panel open over the Dashboard — `?__design=Notifications` (dev-only)
  // reproduces that the same way LeadDetailPage does for ConvertLeadModal.
  const [open, setOpen] = useState(() => import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'Notifications');
  const [tab, setTab] = useState<Tab>('all');
  const { data: unread } = useUnreadCountQuery();
  const { data, isLoading } = useNotificationsQuery(false);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  // Read notifications drop out of this dropdown entirely, not just the
  // "Unread" tab — once you've opened one (or used "Mark all as read"),
  // it's done with here. The full history, read included, is still at
  // /notifications ("View all notifications" below).
  const all = (data?.data ?? []).filter((n) => !n.readAt);
  const hasUnread = (unread?.count ?? 0) > 0;
  const mentionsCount = all.filter((n) => n.type === 'note.mention').length;
  const assignedCount = all.filter((n) => n.type === 'lead.assigned').length;

  const filtered = useMemo(() => {
    switch (tab) {
      // 'all' is already unread-only (see above), so the 'unread' tab has
      // nothing left to filter beyond that — same list, kept as its own tab
      // since "Unread" is still the more honest label for what this is.
      case 'unread':
        return all;
      case 'mentions':
        return all.filter((n) => n.type === 'note.mention');
      case 'assigned':
        return all.filter((n) => n.type === 'lead.assigned');
      default:
        return all;
    }
  }, [all, tab]);

  const groups = useMemo(() => {
    const buckets: { label: 'Today' | 'Earlier'; items: NotificationResponse[] }[] = [];
    for (const n of filtered) {
      const label = groupLabel(n.createdAt);
      const last = buckets.at(-1);
      if (last?.label === label) last.items.push(n);
      else buckets.push({ label, items: [n] });
    }
    return buckets;
  }, [filtered]);

  const openNotification = (n: NotificationResponse) => {
    if (!n.readAt) markRead.mutate(n.id);
    const link = notificationLink(n);
    setOpen(false);
    if (link) navigate({ to: link.to, params: link.params } as never);
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={hasUnread ? `Notifications, ${unread!.count} unread` : 'Notifications'}
        onClick={() => setOpen((o) => !o)}
        className="relative flex size-9 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle"
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
        </svg>
        {hasUnread && <span className="absolute right-2 top-2 size-2 rounded-full border-2 border-ground bg-notification-dot" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <aside role="dialog" aria-label="Notifications" style={{ width: '420px' }} className="fixed right-0 top-15 z-50 flex h-[calc(100vh-60px)] flex-col border-l border-border bg-surface shadow-dialog">
            <div className="flex h-14 shrink-0 items-center gap-2 px-3 pl-5">
              <h2 className="flex-1 text-base font-semibold text-ink">Notifications</h2>
              {hasUnread && (
                <button type="button" onClick={() => markAllRead.mutate()} className="flex h-8 items-center gap-1.5 rounded-chip px-2 text-[13px] font-semibold text-primary-soft-text hover:bg-primary-soft">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 12 5 5 9-10" />
                  </svg>
                  Mark all as read
                </button>
              )}
              <Link to="/admin/notifications" aria-label="Notification settings" onClick={() => setOpen(false)} className="flex size-8 items-center justify-center rounded-chip text-text-secondary hover:bg-surface-subtle">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
                </svg>
              </Link>
            </div>

            <div role="tablist" aria-label="Filter notifications" className="flex shrink-0 gap-5 border-b border-border px-5 text-[13px] font-semibold">
              {(
                [
                  ['all', 'All', 0],
                  ['unread', 'Unread', unread?.count ?? 0],
                  ['mentions', 'Mentions', mentionsCount],
                  ['assigned', 'Assigned', assignedCount],
                ] as const
              ).map(([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-1.5 pb-2.5 ${tab === key ? 'border-b-2 border-primary text-ink' : 'text-text-muted'}`}
                >
                  {label}
                  {count > 0 && (
                    <span className="flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-white">{count}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {isLoading && <p className="px-5 py-3 text-sm text-text-muted">Loading…</p>}
              {!isLoading && filtered.length === 0 && <p className="px-5 py-3 text-sm text-text-muted">You're all caught up.</p>}
              {groups.map((group) => (
                <div key={group.label}>
                  <span className="flex h-8 items-end px-5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">{group.label}</span>
                  {group.items.map((n) => {
                    const style = TYPE_STYLE[n.type] ?? DEFAULT_TYPE_STYLE;
                    const link = notificationLink(n);
                    const alert = ADMIN_ALERT_STYLE[n.type];
                    return (
                      <article
                        key={n.id}
                        className={`flex gap-2.5 border-b border-divider py-3 pl-3 pr-5 ${alert ? 'mx-3 my-3 rounded-input border-0' : ''}`}
                        style={alert ? { background: alert.bg } : !n.readAt ? { background: 'var(--color-primary-tint)' } : undefined}
                      >
                        {!alert && (
                          <span className="flex w-2 shrink-0 justify-center pt-3">{!n.readAt && <span aria-label="Unread" className="size-2 rounded-full bg-primary" />}</span>
                        )}
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-full" style={{ background: alert ? 'var(--color-surface)' : style.bg }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            {style.isRect && <rect x="3" y="7" width="18" height="13" rx="2" />}
                            <path d={style.path} />
                          </svg>
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: alert?.title ?? 'var(--color-ink)' }}>{n.title}</span>
                            {alert && (
                              <span className="flex h-5 items-center gap-1 rounded-xs border px-1.5 text-[11px] font-semibold" style={{ color: alert.title, borderColor: alert.border }}>
                                Admins only
                              </span>
                            )}
                          </div>
                          {n.body && (
                            <p
                              className={`text-[13px] leading-4.5 ${alert ? '' : n.type === 'note.mention' ? 'rounded-input border border-border bg-surface px-2.5 py-2 text-text-secondary' : 'text-text-secondary'}`}
                              style={alert ? { color: alert.body } : undefined}
                            >
                              {n.body}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-xs" style={{ color: alert?.meta ?? 'var(--color-text-muted)' }}>
                            <span>
                              {timeAgo(n.createdAt)} · {style.category}
                            </span>
                            <div className="flex-1" />
                            {link && (
                              <button
                                type="button"
                                onClick={() => openNotification(n)}
                                className={`flex h-6.5 items-center rounded-xs px-2.5 text-xs font-semibold ${alert ? 'border' : 'border border-border-strong text-ink'}`}
                                style={alert ? { color: alert.title, borderColor: alert.border, background: 'var(--color-surface)' } : undefined}
                              >
                                View
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex h-12 shrink-0 items-center justify-between border-t border-border bg-surface-subtle px-5 text-[13px]">
              <Link to="/notifications" onClick={() => setOpen(false)} className="font-semibold text-primary-soft-text hover:underline">
                View all notifications
              </Link>
              <Link to="/admin/notifications" onClick={() => setOpen(false)} className="text-xs text-text-muted hover:underline">
                Manage settings
              </Link>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
